
import { useState, useEffect } from 'react';
import { useSubscription } from '@/hooks/use-subscription';
import { useAuth } from '@/contexts/AuthContext';
import { SubscriptionDetails } from '@/components/subscription/SubscriptionDetails';
import { SubscriptionPlans } from '@/components/subscription/SubscriptionPlans';
import { UpgradeModal } from '@/components/subscription/UpgradeModal';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';
import { SubscriptionPlan } from '@/hooks/use-subscription';

export default function SubscriptionsPage() {
  const { loading, subscription, subscriptionFeatures, fetchUserSubscription } = useSubscription();
  const { isAuthenticated, user, userRole } = useAuth();
  const { toast } = useToast();
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [usageCount, setUsageCount] = useState(0);
  const [activeTab, setActiveTab] = useState('plans');
  const isStudioOwner = userRole === 'studio_owner';

  // Fetch usage stats (number of appointments)
  const fetchUsage = async () => {
    try {
      if (!user) return;
      
      // In a real implementation, this would query actual appointments
      // For now, just return a mock value based on the plan
      let mockUsage = 0;
      if (subscription) {
        if (subscription.plan === 'free' || subscription.plan === 'pro') {
          mockUsage = Math.floor(Math.random() * 5);
        } else if (subscription.plan === 'studio_free') {
          mockUsage = Math.floor(Math.random() * 10);
        } else if (subscription.plan === 'studio_pro') {
          mockUsage = Math.floor(Math.random() * 40) + 10;
        } else if (subscription.plan === 'studio_business') {
          mockUsage = Math.floor(Math.random() * 150) + 100;
        } else {
          mockUsage = Math.floor(Math.random() * 100) + 200;
        }
      }
      setUsageCount(mockUsage);
    } catch (error: any) {
      console.error('Error fetching usage:', error);
    }
  };

  useEffect(() => {
    if (subscription) {
      fetchUsage();
    }
  }, [subscription]);

  if (!isAuthenticated) {
    return (
      <div className="container py-8">
        <Card>
          <CardHeader>
            <CardTitle>Anmeldung erforderlich</CardTitle>
            <CardDescription>Bitte melde dich an, um deine Abonnements zu verwalten.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild>
              <a href="/auth">Zum Login</a>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container py-8 flex justify-center items-center min-h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin text-chrona-accent" />
      </div>
    );
  }

  const handlePlanSelect = async (planName: string) => {
    // Ensure planName is a valid SubscriptionPlan type
    const validPlan = planName as SubscriptionPlan;
    
    // In a real implementation, this would redirect to Stripe
    // For demo purposes, we'll just update the plan directly
    try {
      toast({
        title: "Plan aktualisiert",
        description: `Dein Plan wurde auf ${validPlan} aktualisiert.`,
      });
      
      // Note: In a real implementation, this should be done server-side after payment confirmation
      const { error } = await supabase
        .from('subscriptions')
        .update({ 
          plan: validPlan,
          updated_at: new Date().toISOString() 
        } as any)
        .eq('user_id', user?.id as any);
        
      if (error) throw error;
      
      // Refresh subscription data
      await fetchUserSubscription();
      
    } catch (error: any) {
      console.error('Error updating plan:', error);
      toast({
        title: "Fehler",
        description: `Fehler beim Aktualisieren des Plans: ${error.message}`,
        variant: "destructive",
      });
    }
  };
  
  return (
    <div className="container py-8">
      <h1 className="text-2xl font-bold mb-6">Abonnementverwaltung</h1>
      
      {subscription && (
        <div className="mb-6">
          <SubscriptionDetails 
            subscription={subscription} 
            onUpgrade={() => setIsUpgradeModalOpen(true)}
            usageCount={usageCount}
          />
        </div>
      )}
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList>
          <TabsTrigger value="plans">Verfügbare Pläne</TabsTrigger>
          <TabsTrigger value="features">Features und Limits</TabsTrigger>
        </TabsList>
        
        <TabsContent value="plans" className="pt-6">
          <div className="space-y-6">
            {isStudioOwner ? (
              <div>
                <h2 className="text-xl font-semibold mb-4">Studio-Abonnements</h2>
                <SubscriptionPlans 
                  plans={subscriptionFeatures}
                  currentPlan={subscription?.plan}
                  onSelectPlan={(plan) => setIsUpgradeModalOpen(true)}
                  showCustomerPlans={false}
                />
              </div>
            ) : (
              <div>
                <h2 className="text-xl font-semibold mb-4">Kunden-Abonnements</h2>
                <SubscriptionPlans 
                  plans={subscriptionFeatures}
                  currentPlan={subscription?.plan}
                  onSelectPlan={(plan) => setIsUpgradeModalOpen(true)}
                  showCustomerPlans={true}
                />
              </div>
            )}
          </div>
        </TabsContent>
        
        <TabsContent value="features" className="pt-6">
          <Card>
            <CardHeader>
              <CardTitle>Features nach Abonnement</CardTitle>
              <CardDescription>Vergleiche die Features und Limits der verschiedenen Abonnements</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-4">Feature</th>
                      {subscriptionFeatures
                        .filter(p => isStudioOwner ? !p.isCustomerPlan : p.isCustomerPlan)
                        .map(plan => (
                          <th key={plan.plan} className="text-center py-2 px-4">
                            {plan.plan === subscription?.plan ? (
                              <span className="text-chrona-accent">{getPlanDisplayName(plan.plan)}</span>
                            ) : (
                              getPlanDisplayName(plan.plan)
                            )}
                          </th>
                        ))
                      }
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="py-2 px-4">Preis</td>
                      {subscriptionFeatures
                        .filter(p => isStudioOwner ? !p.isCustomerPlan : p.isCustomerPlan)
                        .map(plan => (
                          <td key={plan.plan} className="text-center py-2 px-4">
                            {plan.monthlyPrice.toFixed(2)}€/m
                          </td>
                        ))
                      }
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 px-4">Buchungslimit</td>
                      {subscriptionFeatures
                        .filter(p => isStudioOwner ? !p.isCustomerPlan : p.isCustomerPlan)
                        .map(plan => (
                          <td key={plan.plan} className="text-center py-2 px-4">
                            {getBucketSizeText(plan.plan, "appointments")}
                          </td>
                        ))
                      }
                    </tr>
                    {isStudioOwner && (
                      <tr className="border-b">
                        <td className="py-2 px-4">Mitarbeiter</td>
                        {subscriptionFeatures
                          .filter(p => !p.isCustomerPlan)
                          .map(plan => (
                            <td key={plan.plan} className="text-center py-2 px-4">
                              {getBucketSizeText(plan.plan, "staff")}
                            </td>
                          ))
                        }
                      </tr>
                    )}
                    <tr className="border-b">
                      <td className="py-2 px-4">Kalender-Export</td>
                      {subscriptionFeatures
                        .filter(p => isStudioOwner ? !p.isCustomerPlan : p.isCustomerPlan)
                        .map(plan => (
                          <td key={plan.plan} className="text-center py-2 px-4">
                            {plan.hasCalendarExport ? "✅" : "❌"}
                          </td>
                        ))
                      }
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 px-4">Add-ons</td>
                      {subscriptionFeatures
                        .filter(p => isStudioOwner ? !p.isCustomerPlan : p.isCustomerPlan)
                        .map(plan => (
                          <td key={plan.plan} className="text-center py-2 px-4">
                            {plan.hasAddons ? "✅" : "❌"}
                          </td>
                        ))
                      }
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 px-4">Analytics</td>
                      {subscriptionFeatures
                        .filter(p => isStudioOwner ? !p.isCustomerPlan : p.isCustomerPlan)
                        .map(plan => (
                          <td key={plan.plan} className="text-center py-2 px-4">
                            {plan.hasAnalytics ? "✅" : "❌"}
                          </td>
                        ))
                      }
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 px-4">Early Access</td>
                      {subscriptionFeatures
                        .filter(p => isStudioOwner ? !p.isCustomerPlan : p.isCustomerPlan)
                        .map(plan => (
                          <td key={plan.plan} className="text-center py-2 px-4">
                            {plan.hasEarlyAccess ? "✅" : "❌"}
                          </td>
                        ))
                      }
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 px-4">Referral Bonus</td>
                      {subscriptionFeatures
                        .filter(p => isStudioOwner ? !p.isCustomerPlan : p.isCustomerPlan)
                        .map(plan => (
                          <td key={plan.plan} className="text-center py-2 px-4">
                            {plan.hasReferralBonus ? "✅" : "❌"}
                          </td>
                        ))
                      }
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      <UpgradeModal 
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        currentPlan={subscription?.plan}
        subscriptionFeatures={subscriptionFeatures}
        onPlanSelect={handlePlanSelect}
        isStudioOwner={isStudioOwner}
      />
    </div>
  );
}

function getPlanDisplayName(plan: string): string {
  switch (plan) {
    case 'free': return 'Kostenlos';
    case 'pro': return 'Pro';
    case 'studio_free': return 'Studio Free';
    case 'studio_pro': return 'Studio Pro';
    case 'studio_business': return 'Studio Business';
    case 'studio_premium': return 'Studio Premium';
    default: return plan;
  }
}

function getBucketSizeText(plan: string, type: "appointments" | "staff"): string {
  if (type === "appointments") {
    switch (plan) {
      case 'free':
      case 'pro':
        return 'Unbegrenzt';
      case 'studio_free':
        return '10/Monat';
      case 'studio_pro':
        return '50/Monat';
      case 'studio_business':
        return '300/Monat';
      case 'studio_premium':
        return 'Unbegrenzt';
      default:
        return '';
    }
  } else if (type === "staff") {
    switch (plan) {
      case 'studio_free':
        return '1';
      case 'studio_pro':
        return '3';
      case 'studio_business':
        return '10';
      case 'studio_premium':
        return 'Unbegrenzt';
      default:
        return '';
    }
  }
  return '';
}
